import React from 'react';
import { Autocomplete, TextField, Box, SxProps, Theme } from '@mui/material';

type WidthVariant = 'full' | 'medium' | 'small';

interface SearchSelectOption {
  id: string | number;
  label: string;
  slug?: string;
}

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
type ChangeHandler<T> = (value: T) => void;

interface SearchSelectProps {
  options: SearchSelectOption[];
  value: string | null;
  onChange: ChangeHandler<string | null>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  width?: WidthVariant;
  /** Field to use as the value (default: 'slug' if present, otherwise 'id') */
  valueField?: 'id' | 'slug';
  /** Minimum width for the dropdown popper (overrides default which matches input width) */
  popperMinWidth?: number;
  sx?: SxProps<Theme>;
}

const getWidthStyles = (width: WidthVariant) => {
  switch (width) {
    case 'small':
      return { width: 250 };
    case 'medium':
      return { width: 'fit-content', minWidth: 300 };
    case 'full':
    default:
      return { width: '100%' };
  }
};

/**
 * SearchSelect component - single-select with search/filter functionality
 * Uses MUI Autocomplete for typeahead search experience
 */
export const SearchSelect: React.FC<SearchSelectProps> = ({
  options,
  value,
  onChange,
  label,
  placeholder = 'Search...',
  required = false,
  disabled = false,
  width = 'medium',
  valueField,
  popperMinWidth,
  sx,
}) => {
  const widthStyles = getWidthStyles(width);

  // Determine which field to use for value matching
  const getValueFromOption = (option: SearchSelectOption): string => {
    if (valueField === 'id') {
      return String(option.id);
    }
    if (valueField === 'slug') {
      return option.slug ?? String(option.id);
    }
    // Auto-detect: use slug if available, otherwise id
    return option.slug ?? String(option.id);
  };

  // Find the selected option based on current value
  const selectedOption = options.find(opt => getValueFromOption(opt) === value) ?? null;

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: SearchSelectOption | null
  ) => {
    onChange(newValue ? getValueFromOption(newValue) : null);
  };

  return (
    <Box sx={{ ...widthStyles, ...sx }}>
      <Autocomplete
        options={options}
        value={selectedOption}
        onChange={handleChange}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, val) => getValueFromOption(option) === getValueFromOption(val)}
        disabled={disabled}
        {...(popperMinWidth ? {
          slotProps: {
            popper: { sx: { minWidth: popperMinWidth } },
            listbox: { sx: { '& .MuiAutocomplete-option': { whiteSpace: 'nowrap' } } },
          },
        } : {})}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            required={required}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.label}
          </li>
        )}
      />
    </Box>
  );
};
